import { CurrencyBlock } from '@/components/CurrencyBlock';
import { WithLoader } from '@/components/WithLoader';
import { useConfigContext } from '@/context/configContext';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { MediaContributionTooltipContent } from '@/components/ContributionTooltips/MediaContributionTooltipContent';
import { MediaInvestmentTooltipContent } from '@/components/ContributionTooltips/MediaInvestmentTooltipContent';
import { TotalContributionTooltipContent } from '@/components/ContributionTooltips/TotalContributionTooltipContent';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';

export const ContributionSummary = () => {
    const {
        data: budgetOptimisationData,
        isLoading,
        isFetching,
        committedOptimizationBase,
    } = useBudgetOptimizationContext();
    const { suggestedBudgetData, contributionTotal, optimized } =
        budgetOptimisationData;

    const totalRecommendedBudget = suggestedBudgetData?.reduce(
        (acc, row) => acc + row.recommendedBudget,
        0,
    );

    const { previousPeriodInvestmentTotal, previous } = budgetOptimisationData;
    const { formatNumber } = useConfigContext();
    const { contributionFormatter } = useDashboardGlobalContext();

    const { totalContribution, mediaContribution } = previous || {};

    const diff = totalRecommendedBudget - previousPeriodInvestmentTotal;
    const percentage =
        Math.round((diff / previousPeriodInvestmentTotal) * 100 * 100) / 100;

    const mediaDiff = contributionTotal - (mediaContribution || 0);
    const mediaPercentage = mediaContribution
        ? Math.round((mediaDiff / mediaContribution) * 100 * 100) / 100
        : 0;

    const getTotalContributionWithNewMedia = () => {
        if (committedOptimizationBase === 'total_kpi' && optimized?.totalKpi) {
            return optimized.totalKpi;
        }

        const baseContribution = totalContribution || 0;
        const oldMediaContribution = mediaContribution || 0;
        return baseContribution - oldMediaContribution + contributionTotal;
    };

    const totalContributionWithNewMedia = getTotalContributionWithNewMedia();

    const contributionDiff =
        totalContributionWithNewMedia - (totalContribution || 0);

    const contributionPercentage = totalContribution
        ? Math.round((contributionDiff / totalContribution) * 100 * 100) / 100
        : 0;

    return (
        <div className="relative glass-tile p-6">
            <WithLoader
                isLoading={isLoading}
                isFetching={isFetching}
                loadingMessage={false}
                delayedMessage={false}
            >
                <div className="flex w-full justify-center gap-6 flex-wrap">
                    <CurrencyBlock
                        className="grow"
                        title="Media investments"
                        titleTooltip="How much money you plan to invest into media over the next year"
                        value={formatNumber(totalRecommendedBudget)}
                        percentage={percentage}
                        absChangeValue={Math.round(diff)}
                        description={
                            <MediaInvestmentTooltipContent
                                formatNumber={formatNumber}
                                previousPeriodInvestmentTotal={
                                    previousPeriodInvestmentTotal
                                }
                                totalRecommendedBudget={totalRecommendedBudget}
                            />
                        }
                    />
                    <CurrencyBlock
                        className="grow"
                        title="Media Contribution"
                        titleTooltip="How much KPI is your media predicted to generate over the next year"
                        value={contributionFormatter(contributionTotal || 0)}
                        percentage={mediaPercentage}
                        absChangeValue={Math.round(mediaDiff)}
                        formatNumber={contributionFormatter}
                        description={
                            <MediaContributionTooltipContent
                                formatNumber={contributionFormatter}
                                mediaContributionTotal={contributionTotal}
                                previousMediaContributionTotal={
                                    mediaContribution || 0
                                }
                                mediaContributionShare={
                                    contributionTotal /
                                    totalContributionWithNewMedia
                                }
                            />
                        }
                    />
                    <CurrencyBlock
                        className="grow"
                        title="Total Contribution"
                        titleTooltip="How much KPI in total (including media) are you predicted to generate over the next year"
                        value={contributionFormatter(
                            totalContributionWithNewMedia,
                        )}
                        percentage={contributionPercentage}
                        absChangeValue={Math.round(contributionDiff)}
                        formatNumber={contributionFormatter}
                        description={
                            <TotalContributionTooltipContent
                                formatNumber={contributionFormatter}
                                totalContribution={
                                    totalContributionWithNewMedia
                                }
                                previousTotalContribution={
                                    totalContribution || 0
                                }
                            />
                        }
                    />
                </div>
            </WithLoader>
        </div>
    );
};
